import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Spin, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { CheckCircleOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import apiClient from '../services/apiClient';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { providerColors, providerIcons, providerNames } from './cloudExecutive/shared';
import { Provider } from './cloudExecutive/types';
import './ClientHome.css';

interface ReportSummary {
  monthlyAverageCost?: number;
  netMonthlySavings?: number;
}

interface CloudAccount {
  id: string;
  provider: Provider;
  name: string;
  providerAccountId?: string;
  subscriptionId?: string;
  lastReportCreatedAt?: string;
  lastReportSummary?: ReportSummary;
  metadata?: {
    reportCount?: number;
    customerName?: string;
  };
}

interface RecommendationBucket {
  identifiedMonthly: number;
  implementedMonthly: number;
  openCount: number;
  implementedCount: number;
  dismissedCount: number;
  currency?: string;
}

type CurrencyOfProvider = Record<Provider, 'USD' | 'BRL'>;

const currencyOfProvider: CurrencyOfProvider = { aws: 'USD', azure: 'BRL', gcp: 'BRL' };

const connectDescriptions: Record<Provider, string> = {
  aws: 'Conecte via role de leitura (assume-role), sem credenciais estáticas.',
  azure: 'Login via OAuth com a sua conta corporativa Microsoft.',
  gcp: 'Conecte via Workload Identity Federation, sem chaves de serviço.'
};

const dateFormatter = new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });

const formatByCurrency = (value: number, currency: 'USD' | 'BRL') =>
  new Intl.NumberFormat(currency === 'BRL' ? 'pt-BR' : 'en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2
  }).format(value || 0);

const providerAccountLabel = (account: CloudAccount) => account.providerAccountId || account.subscriptionId || account.name;

// The client identity, distinct from the account's own nickname: two
// accounts (even across providers) tagged with the same customerName at
// connect time belong to the same client and should visually group
// together. Falls back to the account's own name when customerName was
// never set, so a solo account looks exactly like it did before grouping.
const clientLabelFor = (account: CloudAccount) => account.metadata?.customerName?.trim() || account.name || providerAccountLabel(account);

const ClientHome: React.FC = () => {
  const { isDark } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState<CloudAccount[]>([]);
  const [totalsByCurrency, setTotalsByCurrency] = useState<Record<string, RecommendationBucket>>({});
  const [totalsByAccount, setTotalsByAccount] = useState<Record<string, RecommendationBucket>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);

      try {
        // ownTenantOnly forces single-tenant scoping even for platform-admin,
        // whose other list views intentionally span every tenant - the home
        // page is "my tenant's summary", never a cross-tenant dump.
        const accountsResponse = await apiClient.get('/cloud/accounts', { params: { limit: 100, ownTenantOnly: true } });
        if (cancelled) return;
        setAccounts((accountsResponse.data?.accounts || []).filter((account: CloudAccount) => account.provider));
      } catch (loadError) {
        console.error('Erro ao carregar contas conectadas na home:', loadError);
        if (!cancelled) setError('Não foi possível carregar suas contas conectadas.');
      }

      // Recommendation totals are additive: if they fail (e.g. still rolling
      // out), the home page should still render with those two metrics at
      // zero rather than failing entirely.
      try {
        const summaryResponse = await apiClient.get('/recommendations/summary');
        if (cancelled) return;
        setTotalsByCurrency(summaryResponse.data?.totals || {});
        setTotalsByAccount(summaryResponse.data?.byAccount || {});
      } catch (summaryError) {
        console.warn('Totais de recomendações indisponíveis na home:', summaryError);
      }

      if (!cancelled) setLoading(false);
    };

    load();
    return () => { cancelled = true; };
  }, []);

  const accountsByProvider = useMemo(() => {
    const grouped: Record<Provider, CloudAccount[]> = { aws: [], azure: [], gcp: [] };
    accounts.forEach(account => {
      if (grouped[account.provider]) grouped[account.provider].push(account);
    });
    return grouped;
  }, [accounts]);

  const openPotentialByCurrency = useMemo(() => {
    const totals: Record<string, number> = {};
    accounts.forEach(account => {
      const currency = currencyOfProvider[account.provider];
      totals[currency] = (totals[currency] || 0) + Number(account.lastReportSummary?.netMonthlySavings || 0);
    });
    return totals;
  }, [accounts]);

  const currenciesInUse = useMemo(() => {
    const currencies = new Set<string>();
    accounts.forEach(account => currencies.add(currencyOfProvider[account.provider]));
    Object.keys(totalsByCurrency).forEach(currency => currencies.add(currency));
    return Array.from(currencies).sort((a, b) => (a === 'BRL' ? -1 : 1) - (b === 'BRL' ? -1 : 1));
  }, [accounts, totalsByCurrency]);

  // A metric card shows one line per currency the user has connected accounts
  // in (e.g. BRL for GCP/Azure, USD for AWS). Rendering every currency
  // unconditionally meant a lone "$0.00" (or similar) could show up whenever
  // that currency's total for this specific metric happened to be zero, with
  // nothing to say what it even was - easy to mistake for a bug. This skips a
  // zero-value currency once there's already a non-zero one to show, and
  // labels every secondary line with its currency code so a real (non-zero)
  // second amount is never ambiguous either.
  const currencyLinesFor = (valueByCurrency: (currency: string) => number) => {
    const lines = currenciesInUse.map(currency => ({ currency, value: valueByCurrency(currency) }));
    const nonZero = lines.filter(line => line.value !== 0);
    return nonZero.length > 0 ? nonZero : lines.slice(0, 1);
  };

  const rows = useMemo(() =>
    [...accounts].sort((a, b) => {
      const clientCompare = clientLabelFor(a).localeCompare(clientLabelFor(b));
      if (clientCompare !== 0) return clientCompare;
      return String(b.lastReportCreatedAt || '').localeCompare(String(a.lastReportCreatedAt || ''));
    }),
  [accounts]);

  // Rows are sorted so every client's accounts are consecutive; the first
  // row of each group carries the full group size as its rowSpan (merging
  // the "Conta" cell visually across every subscription/cloud of that
  // client) and every following row in the same group carries 0, which antd
  // treats as "hidden, absorbed into the cell above".
  const clientRowSpanById = useMemo(() => {
    const spans = new Map<string, number>();
    for (let index = 0; index < rows.length; index += 1) {
      const account = rows[index];
      const isGroupStart = index === 0 || clientLabelFor(rows[index - 1]) !== clientLabelFor(account);
      if (!isGroupStart) {
        spans.set(account.id, 0);
        continue;
      }
      let groupSize = 1;
      while (index + groupSize < rows.length && clientLabelFor(rows[index + groupSize]) === clientLabelFor(account)) {
        groupSize += 1;
      }
      spans.set(account.id, groupSize);
    }
    return spans;
  }, [rows]);

  const columns: ColumnsType<CloudAccount> = [
    {
      title: 'Conta',
      key: 'client',
      onCell: account => ({ rowSpan: clientRowSpanById.get(account.id) ?? 1 }),
      render: (_, account) => <strong>{clientLabelFor(account)}</strong>
    },
    {
      title: 'Nuvem',
      key: 'provider',
      render: (_, account) => {
        const ownName = account.name || providerAccountLabel(account);
        const showOwnName = ownName && ownName !== clientLabelFor(account);
        return (
          <span className="client-home__cell-provider">
            <span className="client-home__dot" style={{ background: providerColors[account.provider] }} />
            {providerNames[account.provider].replace(' FinOps', '')}
            {showOwnName && <span className="client-home__account-sublabel"> · {ownName}</span>}
          </span>
        );
      }
    },
    {
      title: 'Data do relatório',
      key: 'date',
      render: (_, account) => account.lastReportCreatedAt ? dateFormatter.format(new Date(account.lastReportCreatedAt)) : '—'
    },
    {
      title: 'Custo médio/mês',
      key: 'cost',
      render: (_, account) => formatByCurrency(Number(account.lastReportSummary?.monthlyAverageCost || 0), currencyOfProvider[account.provider])
    },
    {
      title: 'Potencial aberto',
      key: 'open',
      render: (_, account) => (
        <span className="client-home__amber">{formatByCurrency(Number(account.lastReportSummary?.netMonthlySavings || 0), currencyOfProvider[account.provider])}</span>
      )
    },
    {
      title: 'Já identificado',
      key: 'identified',
      render: (_, account) => {
        const bucket = totalsByAccount[account.id];
        if (!bucket) return <span className="client-home__muted">—</span>;
        return <span className="client-home__blue">{formatByCurrency(bucket.identifiedMonthly, currencyOfProvider[account.provider])}</span>;
      }
    },
    {
      title: 'Implementado',
      key: 'implemented',
      render: (_, account) => {
        const bucket = totalsByAccount[account.id];
        if (!bucket || bucket.implementedCount === 0) return <span className="client-home__muted">—</span>;
        return <span className="client-home__green">{formatByCurrency(bucket.implementedMonthly, currencyOfProvider[account.provider])}</span>;
      }
    },
    {
      title: '',
      key: 'link',
      render: (_, account) => <Button type="link" size="small" onClick={() => navigate('/reports')}>Ver →</Button>
    }
  ];

  if (loading) {
    return (
      <div className={`client-home ${isDark ? 'client-home--dark' : ''}`}>
        <div className="client-home__loading"><Spin size="large" /></div>
      </div>
    );
  }

  if (accounts.length === 0) {
    return (
      <div className={`client-home ${isDark ? 'client-home--dark' : ''}`}>
        <div className="client-home__header">
          <div className="client-home__eyebrow">Visão geral</div>
          <h1 className="client-home__title">Bem-vindo, seja bem-vindo</h1>
          <p className="client-home__subtitle">Conecte sua primeira conta de nuvem para começar a identificar oportunidades de economia.</p>
        </div>

        <div className="client-home__empty-card">
          <h2>Nenhuma nuvem conectada ainda</h2>
          <p>Escolha um provedor abaixo para conectar em modo somente leitura. Leva menos de 5 minutos e o primeiro relatório fica pronto no mesmo dia.</p>

          <div className="client-home__connect-grid">
            {(['aws', 'azure', 'gcp'] as Provider[]).map(provider => (
              <div className="client-home__connect-card" key={provider}>
                <div className="client-home__connect-badge" style={{ background: providerColors[provider] }}>
                  {providerIcons[provider]}
                </div>
                <div className="client-home__connect-name">{providerNames[provider]}</div>
                <div className="client-home__connect-description">{connectDescriptions[provider]}</div>
                <button type="button" className="client-home__connect-button" style={{ background: providerColors[provider] }} onClick={() => navigate('/connect')}>
                  Conectar {providerNames[provider].replace(' FinOps', '')}
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`client-home ${isDark ? 'client-home--dark' : ''}`}>
      <div className="client-home__header">
        <div>
          <div className="client-home__eyebrow">Visão geral</div>
          <h1 className="client-home__title">Bem-vindo de volta{user?.name ? `, ${user.name}` : ''}</h1>
          <p className="client-home__subtitle">Resumo consolidado dos relatórios já gerados nas suas nuvens conectadas.</p>
        </div>
        <div className="client-home__badge">
          <CheckCircleOutlined /> {accounts.length} conta{accounts.length === 1 ? '' : 's'} conectada{accounts.length === 1 ? '' : 's'}
        </div>
      </div>

      {error && <Alert type="warning" showIcon message={error} style={{ marginBottom: 16 }} />}

      <div className="client-home__metrics">
        <div className="client-home__metric-card client-home__metric-card--amber">
          <div className="client-home__metric-label client-home__metric-label--amber">Potencial aberto agora</div>
          {currencyLinesFor(currency => openPotentialByCurrency[currency] || 0).map(({ currency }, index) => (
            <div key={currency} className={index === 0 ? 'client-home__metric-value' : 'client-home__metric-secondary'}>
              {formatByCurrency(openPotentialByCurrency[currency] || 0, currency as 'USD' | 'BRL')}
              {index === 0 ? <span className="client-home__metric-unit">/mês</span> : <span className="client-home__metric-unit">/mês ({currency})</span>}
            </div>
          ))}
          <div className="client-home__metric-note">Oportunidades identificadas no relatório mais recente de cada conta, ainda sem ação.</div>
        </div>

        <div className="client-home__metric-card client-home__metric-card--blue">
          <div className="client-home__metric-label client-home__metric-label--blue">Já identificado no total</div>
          {currencyLinesFor(currency => totalsByCurrency[currency]?.identifiedMonthly || 0).map(({ currency }, index) => (
            <div key={currency} className={index === 0 ? 'client-home__metric-value' : 'client-home__metric-secondary'}>
              {formatByCurrency(totalsByCurrency[currency]?.identifiedMonthly || 0, currency as 'USD' | 'BRL')}
              {index === 0 ? <span className="client-home__metric-unit">/mês</span> : <span className="client-home__metric-unit">/mês ({currency})</span>}
            </div>
          ))}
          <div className="client-home__metric-note">Soma de todas as oportunidades únicas encontradas pela IA desde a primeira análise, sem repetir a mesma oportunidade em relatórios diferentes.</div>
        </div>

        <div className="client-home__metric-card client-home__metric-card--green">
          <div className="client-home__metric-label client-home__metric-label--green">Economia implementada</div>
          {currencyLinesFor(currency => totalsByCurrency[currency]?.implementedMonthly || 0).map(({ currency }, index) => (
            <div key={currency} className={index === 0 ? 'client-home__metric-value client-home__metric-value--green' : 'client-home__metric-secondary client-home__metric-secondary--green'}>
              {formatByCurrency(totalsByCurrency[currency]?.implementedMonthly || 0, currency as 'USD' | 'BRL')}
              {index === 0 ? <span className="client-home__metric-unit">/mês</span> : <span className="client-home__metric-unit">/mês ({currency})</span>}
            </div>
          ))}
          <div className="client-home__metric-note">Recomendações que não aparecem mais no relatório mais recente — sinal de que já foram aplicadas.</div>
        </div>
      </div>

      <div className="client-home__table-card">
        <div className="client-home__table-header">
          <div>
            <span className="client-home__table-title">Suas contas conectadas</span>
            <span className="client-home__table-subtitle">
              {accounts.reduce((sum, account) => sum + Number(account.metadata?.reportCount || 0), 0)} relatórios gerados
            </span>
          </div>
          <Button type="link" onClick={() => navigate('/reports')}>Ver todos os relatórios</Button>
        </div>
        <Table
          columns={columns}
          dataSource={rows}
          rowKey="id"
          pagination={false}
        />
      </div>

      {accountsByProvider.azure.length > 0 && (
        <p className="client-home__footnote">
          "Já identificado" e "Implementado" ainda não refletem contas Azure — falta rastreamento individual de recomendações para esse provedor.
        </p>
      )}
    </div>
  );
};

export default ClientHome;
